import { Users } from 'lucide-react';
import { useState } from 'react';
import { ApplicantRoleModel } from '../../models/ApplicantRole';
import { EventReferentModel } from '../../models/Evento';
import { EventReferentPayload } from '../../services/useEventoService';
import ReferentFormModal from './ReferentFormModal';
import { EyeButton, PlusButton, TrashButton } from 'shared/components/IconButtons';
import DeleteModal from 'shared/components/DeleteModal';

interface Props {
  referents: EventReferentModel[];
  roles: ApplicantRoleModel[];
  isSaving: boolean;
  isRemoving: boolean;
  onAdd: (payload: EventReferentPayload) => void;
  onUpdate: (referentId: number, payload: EventReferentPayload) => void;
  onRemove: (referentId: number) => Promise<void>;
}

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const EventoReferentsSection = ({
  referents,
  roles,
  isSaving,
  isRemoving,
  onAdd,
  onUpdate,
  onRemove
}: Props) => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    referent: EventReferentModel | null;
    onlyView: boolean;
  }>({ open: false, referent: null, onlyView: false });
  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    referentId: number;
  }>({ open: false, referentId: 0 });

  return (
    <div className="min-w-0 rounded-2xl border border-white bg-whiteLight p-6 dark:border-white/10 dark:bg-darkLight">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-secondary-500" />
          <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
            Referentes
          </h2>
          <span className="rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs font-medium text-secondary-700 dark:bg-white/10 dark:text-secondary-200">
            {referents.length}
          </span>
        </div>
        <PlusButton
          size={24}
          onClick={() => setModalState({ open: true, referent: null, onlyView: false })}
        />
      </div>

      <div className="flex max-h-60 flex-col gap-3 overflow-y-auto pr-1">
        {referents.length === 0 && (
          <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
            Todavía no hay referentes cargados.
          </p>
        )}
        {referents.map((referent) => (
          <div
            key={referent.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-secondary-50 px-4 py-3 dark:border-white/5"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-semibold text-secondary-700 dark:bg-white/10 dark:text-secondary-200">
                {initials(referent.name)}
              </span>
              <div>
                <p className="text-sm font-medium text-ink dark:text-ink-dark">
                  {referent.name}
                </p>
                <p className="text-xs text-inkMuted dark:text-inkMuted-dark">
                  {referent.role?.name ?? 'Sin rol'}
                  {referent.email ? ` · ${referent.email}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <EyeButton
                onClick={() => setModalState({ open: true, referent, onlyView: true })}
              />
              <TrashButton
                onClick={() => setDeleteModalState({ open: true, referentId: referent.id })
                }
              />
            </div>
          </div>
        ))}
      </div>

      <ReferentFormModal
        open={modalState.open}
        roles={roles}
        referent={modalState.referent}
        onlyView={modalState.onlyView}
        onEdit={() => setModalState({ open: true, referent: modalState.referent, onlyView: false })}
        isSaving={isSaving}
        onClose={() => setModalState({ open: false, referent: null, onlyView: false })}
        onSave={(payload) => {
          if (modalState.referent) {
            onUpdate(modalState.referent.id, payload);
          } else {
            onAdd(payload);
          }
          setModalState({ open: false, referent: null, onlyView: false });
        }}
      />
      <DeleteModal
        open={deleteModalState.open}
        onClose={() => setDeleteModalState({ open: false, referentId: 0 })}
        title="¿Eliminar este referente?"
        body="El referente dejará de aparecer en el evento."
        isLoading={isRemoving}
        onRemove={async () => {
          try {
            await onRemove(deleteModalState.referentId);
            setDeleteModalState({ open: false, referentId: 0 });
          } catch (_) {
            // El aviso general muestra el error y permite reintentar.
          }
        }}
      />
    </div>
  );
};

export default EventoReferentsSection;
